// src/components/Profile/PerfilNegocio.jsx
import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  Alert,
  Tabs,
  Tab,
  Table,
  Badge,
} from "react-bootstrap";
import { QRCodeCanvas } from "qrcode.react";
import api from "../../api/axios";

// 🖼️ Imágenes de tiers de negocio
import activoTier from "../../assets/tiers/activo.png";
import destacadoTier from "../../assets/tiers/destacado.png";
import premiumTier from "../../assets/tiers/premium.png";
import eliteTier from "../../assets/tiers/elite.png";

// Tiers negocio
const TIERS = [
  { code: "ACTIVO", label: "Activo", min: 0 },
  { code: "DESTACADO", label: "Destacado", min: 50 },
  { code: "PREMIUM", label: "Premium", min: 150 },
  { code: "ELITE", label: "Elite", min: 400 },
];

const TIER_EMOJI = {
  ACTIVO: "📌",
  DESTACADO: "⭐",
  PREMIUM: "💎",
  ELITE: "👑",
};

const TIER_IMAGES = {
  ACTIVO: activoTier,
  DESTACADO: destacadoTier,
  PREMIUM: premiumTier,
  ELITE: eliteTier,
};

function getBusinessTierFromPoints(totalPoints) {
  if (totalPoints >= 400) return "ELITE";
  if (totalPoints >= 150) return "PREMIUM";
  if (totalPoints >= 50) return "DESTACADO";
  return "ACTIVO";
}

function getTierMeta(tierCode, totalPoints) {
  let code = tierCode || getBusinessTierFromPoints(totalPoints);
  code = code.toUpperCase();

  let idx = TIERS.findIndex((t) => t.code === code);
  if (idx === -1) {
    code = getBusinessTierFromPoints(totalPoints);
    idx = TIERS.findIndex((t) => t.code === code);
  }

  const current = TIERS[idx] || TIERS[0];
  const next = TIERS[idx + 1] || null;

  return {
    code: current.code,
    name: current.label,
    min: current.min,
    nextMin: next ? next.min : null,
    emoji: TIER_EMOJI[current.code] || "🏅",
  };
}

// =============== Historial negocio ===============
const HistorialNegocio = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const token = localStorage.getItem("token");

    const fetchHistory = async () => {
      try {
        const res = await api.get("/points/business/history", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res.data;
        setItems(data.items || []);
      } catch (err) {
        console.error("Error cargando historial de negocio:", err);
        setError("No pudimos cargar el historial de puntos del local.");
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [pageSize, items.length]);

  const formatDateTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString("es-AR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const pageItems = items.slice(start, start + pageSize);

  return (
    <Card className="mt-3">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-end mb-3 gap-2">
          <h5 className="mb-0">Historial de puntos del local</h5>
          {!loading && !error && items.length > 0 && (
            <div style={{ width: 170 }}>
              <small className="text-muted d-block mb-1">Por página</small>
              <select
                className="form-select form-select-sm"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
            </div>
          )}
        </div>

        {loading && (
          <div className="my-2">
            {Array.from({ length: 5 }).map((_, idx) => (
              <div
                key={idx}
                className="d-flex justify-content-between align-items-center py-2 border-bottom"
              >
                <div style={{ width: "70%" }}>
                  <div
                    style={{
                      height: 12,
                      width: "55%",
                      background: "#e5e7eb",
                      borderRadius: 4,
                      marginBottom: 8,
                    }}
                  />
                  <div
                    style={{
                      height: 10,
                      width: "40%",
                      background: "#e5e7eb",
                      borderRadius: 4,
                    }}
                  />
                </div>
                <div
                  style={{
                    height: 12,
                    width: 64,
                    background: "#e5e7eb",
                    borderRadius: 4,
                  }}
                />
              </div>
            ))}
          </div>
        )}

        {error && !loading && (
          <Alert variant="warning" className="mb-0">
            {error}
          </Alert>
        )}

        {!loading && !error && items.length === 0 && (
          <p className="text-muted mb-0">
            Todavía no se registraron movimientos de puntos en este local.
          </p>
        )}

        {!loading && !error && items.length > 0 && (
          <ul className="list-unstyled mb-0">
            {pageItems.map((mov) => (
              <li key={mov.id} className="mb-3 pb-2 border-bottom">
                <div className="d-flex justify-content-between">
                  <div>
                    <strong>
                      {mov.description || "Movimiento de puntos"}
                    </strong>
                    <br />
                    <small className="text-muted">
                      {formatDateTime(mov.created_at)}
                    </small>
                  </div>
                  <div className="text-end">
                    <span
                      style={{
                        fontWeight: "bold",
                        color: mov.points >= 0 ? "green" : "red",
                      }}
                    >
                      {mov.points >= 0 ? `+${mov.points}` : mov.points} pts
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {!loading && !error && items.length > 0 && (
          <div className="d-flex justify-content-between align-items-center mt-2">
            <small className="text-muted">
              Mostrando {start + 1}-{Math.min(start + pageSize, total)} de {total}
            </small>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={safePage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={safePage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

// =============== Ranking clientes ===============
const RankingClientes = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");

    const fetchRanking = async () => {
      try {
        const res = await api.get("/points/business/ranking/users", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res.data;
        setItems(data.items || []);
      } catch (err) {
        console.error("Error cargando ranking de clientes:", err);
        setError("No pudimos cargar el ranking de clientes.");
      } finally {
        setLoading(false);
      }
    };

    fetchRanking();
  }, []);

  if (loading) {
    return (
      <Card className="mt-3">
        <Card.Body>
          <div
            style={{
              height: 14,
              width: "45%",
              background: "#e5e7eb",
              borderRadius: 4,
              marginBottom: 12,
            }}
          />
          {Array.from({ length: 5 }).map((_, idx) => (
            <div
              key={idx}
              style={{
                display: "grid",
                gridTemplateColumns: "0.5fr 1.8fr 1fr 1fr",
                gap: "0.75rem",
                alignItems: "center",
                padding: "0.55rem 0",
                borderBottom: idx === 4 ? "none" : "1px solid #e5e7eb",
              }}
            >
              <div style={{ height: 10, background: "#e5e7eb", borderRadius: 4 }} />
              <div style={{ height: 10, background: "#e5e7eb", borderRadius: 4 }} />
              <div style={{ height: 10, background: "#e5e7eb", borderRadius: 4 }} />
              <div style={{ height: 10, background: "#e5e7eb", borderRadius: 4 }} />
            </div>
          ))}
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="mt-3">
        <Card.Body>
          <Alert variant="warning" className="mb-0">
            {error}
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card className="mt-3">
        <Card.Body>
          <h5 className="mb-3">Ranking de clientes</h5>
          <p className="text-muted mb-0">
            Todavía no hay clientes con puntos en este local.
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mt-3">
      <Card.Body>
        <h5 className="mb-3">Ranking de clientes</h5>
        <Table responsive striped hover size="sm">
          <thead>
            <tr>
              <th>#</th>
              <th>Cliente</th>
              <th>Puntos</th>
              <th>Movimientos</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row, index) => (
              <tr key={row.user_id}>
                <td>
                  <strong>{index + 1}</strong>
                </td>
                <td>
                  <div>
                    {row.user_name || "Sin nombre"}
                    {index === 0 && (
                      <Badge bg="warning" text="dark" className="ms-2">
                        Top 1
                      </Badge>
                    )}
                  </div>
                  {row.user_email && (
                    <div className="text-muted" style={{ fontSize: ".8rem" }}>
                      {row.user_email}
                    </div>
                  )}
                </td>
                <td>
                  <strong>{row.total_points}</strong> pts
                </td>
                <td>{row.movements_count}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

// =============== Tab Catálogo público ===============
const CatalogoTab = ({ businessSlug }) => {
  let storedBusiness = null;
  try {
    storedBusiness = JSON.parse(localStorage.getItem("business") || "null");
  } catch (e) {
    storedBusiness = null;
  }

  const slug =
    businessSlug ||
    storedBusiness?.slug ||
    localStorage.getItem("slug");

  if (!slug) {
    return (
      <Card className="mt-3">
        <Card.Body>
          <h5 className="mb-2">Catálogo público</h5>
          <p className="text-muted mb-0">
            Todavía no pudimos detectar el enlace de tu catálogo.
            Guardá primero el perfil del local (slug) y volvé a esta sección.
          </p>
        </Card.Body>
      </Card>
    );
  }

  const catalogUrl = `${window.location.origin}/catalogo/${slug}`;

  return (
    <Card className="mt-3 shadow-sm">
      <Card.Body>
        <h5 className="mb-2">Tu catálogo público</h5>
        <p className="text-muted mb-3">
          Compartí este enlace con tus clientes para que vean tus beneficios activos.
        </p>

        <div className="d-flex flex-column flex-md-row gap-3 align-items-start align-items-md-center">
          <div className="flex-grow-1 w-100">
            <input
              className="form-control"
              value={catalogUrl}
              readOnly
              onClick={(e) => e.target.select()}
            />
            <small className="text-muted">
              Tip: hacé clic para seleccionar y copiá el link.
            </small>
          </div>

          <div className="text-center">
            <QRCodeCanvas value={catalogUrl} size={140} />
            <div className="mt-2">
              <a
                href={catalogUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-primary btn-sm"
              >
                Ver catálogo público
              </a>
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

// =============== PerfilNegocio (main) ===============
const PerfilNegocio = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [business, setBusiness] = useState(null);
  const [pointsTotal, setPointsTotal] = useState(0);
  const [tierCode, setTierCode] = useState(null);

  // fallback localStorage por si las dudas con el logo
  let storedBusiness = null;
  try {
    storedBusiness = JSON.parse(localStorage.getItem("business") || "null");
  } catch (e) {
    storedBusiness = null;
  }

  useEffect(() => {
    const token = localStorage.getItem("token");

    const fetchSummary = async () => {
      try {
        const res = await api.get("/points/business/me", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res.data;

        setBusiness(data.business || null);
        setPointsTotal(data.points_total ?? 0);
        setTierCode(data.tier_code ?? null);
      } catch (err) {
        console.error("Error cargando resumen del negocio:", err);
        setError("No pudimos cargar el resumen de puntos del local.");
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, []);

  // Skeleton general del header
  if (loading) {
    return (
      <Container className="mt-4">
        <Card className="mb-3 shadow-sm">
          <Card.Body className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
            <div className="d-flex align-items-center gap-3 w-100">
              {/* logo skeleton */}
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: "#e5e7eb",
                }}
              />
              <div className="flex-grow-1">
                <div
                  style={{
                    height: 16,
                    width: "40%",
                    background: "#e5e7eb",
                    borderRadius: 4,
                    marginBottom: 8,
                  }}
                />
                <div
                  style={{
                    height: 12,
                    width: "25%",
                    background: "#e5e7eb",
                    borderRadius: 4,
                  }}
                />
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Tabs skeleton */}
        <Card className="mt-3">
          <Card.Body>
            <div
              style={{
                height: 12,
                width: "30%",
                background: "#e5e7eb",
                borderRadius: 4,
                marginBottom: 10,
              }}
            />
            <div
              style={{
                height: 10,
                width: "100%",
                background: "#e5e7eb",
                borderRadius: 4,
              }}
            />
          </Card.Body>
        </Card>
      </Container>
    );
  }

  const tierMeta = getTierMeta(tierCode, pointsTotal);
  const tierImgSrc = TIER_IMAGES[tierMeta.code];

  const effectiveBusiness = business || storedBusiness || {};
  const effectiveLogo = effectiveBusiness.logo_url || storedBusiness?.logo_url || null;

  let progressPercent = 100;
  let remainingToNext = 0;

  if (tierMeta.nextMin !== null) {
    const range = tierMeta.nextMin - tierMeta.min;
    const currentInRange = Math.max(
      0,
      Math.min(pointsTotal - tierMeta.min, range)
    );
    progressPercent = (currentInRange / range) * 100;
    remainingToNext = Math.max(0, tierMeta.nextMin - pointsTotal);
  }

  return (
    <Container className="mt-4">
      {/* Card resumen del local */}
 <Card className="mb-3 shadow-sm">
  <Card.Body>
    {/* FILA 1: logo + nombre ----- tier a la derecha */}
    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-3">
      {/* Izquierda: logo + nombre */}
      <div className="d-flex align-items-center gap-3 mb-2 mb-md-0">
        {effectiveLogo && (
          <img
            src={effectiveLogo}
            alt={effectiveBusiness.name || "Logo del local"}
            style={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
        )}
        <div>
          <h5 className="mb-0">
            {effectiveBusiness.name || "Mi local"}
          </h5>
        </div>
      </div>

      {/* Derecha: badge + nombre de tier */}
      <div className="d-flex align-items-center gap-3">
        {tierImgSrc && (
          <img
            src={tierImgSrc}
            alt={tierMeta.name}
            style={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              objectFit: "cover",
              boxShadow: "0 0 10px rgba(0,0,0,0.15)",
            }}
          />
        )}
        <div className="text-md-end text-center">
          <div className="text-muted" style={{ fontSize: ".85rem" }}>
            Tier actual
          </div>
          <div style={{ fontSize: "1rem", fontWeight: 600 }}>
            {tierMeta.name}
          </div>
        </div>
      </div>
    </div>

    {/* FILA 2: puntos centrados */}
    <div className="text-center mb-1">
      <span style={{ fontSize: "1.1rem", fontWeight: "bold" }}>
        {pointsTotal}
      </span>{" "}
      <span className="text-muted" style={{ fontSize: ".9rem" }}>
        puntos acumulados
      </span>
    </div>

    {/* FILA 3: barra de progreso a lo ancho de la card */}
    <div className="w-100 mb-1">
      <div
        className="progress"
        style={{ height: 6, borderRadius: 999 }}
      >
        <div
          className="progress-bar"
          role="progressbar"
          style={{ width: `${progressPercent}%` }}
          aria-valuenow={progressPercent}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>

    {/* FILA 4: texto inferior centrado */}
    <div className="text-center">
      {tierMeta.nextMin !== null ? (
        <small className="text-muted">
          Te faltan <strong>{remainingToNext}</strong> puntos para el próximo
          tier.
        </small>
      ) : (
        <small className="text-muted">
          Sos <strong>{tierMeta.name}</strong>, máximo tier 🎉
        </small>
      )}
    </div>

    {(effectiveBusiness.address || effectiveBusiness.city || effectiveBusiness.postal_code) && (
      <div className="text-center mt-3">
        <small className="text-muted d-block">
          {[effectiveBusiness.address, effectiveBusiness.city, effectiveBusiness.state]
            .filter(Boolean)
            .join(", ")}
          {effectiveBusiness.postal_code ? ` (${effectiveBusiness.postal_code})` : ""}
        </small>
        {effectiveBusiness.latitude && effectiveBusiness.longitude && (
          <a
            href={`https://www.google.com/maps?q=${effectiveBusiness.latitude},${effectiveBusiness.longitude}`}
            target="_blank"
            rel="noreferrer"
            className="btn btn-outline-primary btn-sm mt-2"
          >
            Ver ubicacion en mapa
          </a>
        )}
      </div>
    )}

    {/* Error (si lo hubiera) abajo del todo */}
    {error && (
      <Alert
        variant="warning"
        className="mt-3 mb-0 py-1 px-2 text-center"
        style={{ fontSize: ".85rem" }}
      >
        {error}
      </Alert>
    )}
  </Card.Body>
</Card>


      {/* Tabs: historial + ranking + catálogo */}
      <Tabs defaultActiveKey="history" className="mb-3">
        <Tab eventKey="history" title="Historial de puntos">
          <HistorialNegocio />
        </Tab>
        <Tab eventKey="ranking" title="Ranking de clientes">
          <RankingClientes />
        </Tab>
        <Tab eventKey="catalog" title="Catálogo público">
          <CatalogoTab businessSlug={effectiveBusiness.slug} />
        </Tab>
      </Tabs>
    </Container>
  );
};

export default PerfilNegocio;
